import { StrictMode } from 'react';

import './index.css';
import './lib/i18n';
import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import ReactDOM from 'react-dom/client';

import NotFound from './components/error/NotFound';
import Spinner from './components/icons/Spinner';
import { SENTRY } from './config';
import Error from '~/components/error/Error';
import { shouldRetryRequest } from '~/queryUtils/shouldRetryRequest';
import { routeTree } from '~/routeTree.gen';
import { getLeadDetailsId } from '~/utils/getLeadDetailsId';
import { shouldKeepScrollPosition } from '~/utils/shouldKeepScrollPosition';

const queryClient = new QueryClient({
    defaultOptions: { mutations: { retry: false }, queries: { retry: shouldRetryRequest } },
});

export const router = createRouter({
    context: {
        queryClient,
    },
    defaultErrorComponent: () => <Error />,
    defaultNotFoundComponent: () => <NotFound />,
    defaultPendingComponent: () => <Spinner />,
    defaultPreload: 'intent',
    routeTree,
});

// ponytail: search-only navigations and tab switches within one lead keep the scroll position unless `page` changes; also overrides an explicit resetScroll: true
const getPage = (search: Record<string, unknown>) => search.page;
router.subscribe('onBeforeRouteMount', ({ fromLocation, pathChanged, toLocation }) => {
    if (
        shouldKeepScrollPosition({
            fromLeadId: getLeadDetailsId(fromLocation.pathname),
            fromPage: getPage(fromLocation.search),
            pathChanged,
            toLeadId: getLeadDetailsId(toLocation.pathname),
            toPage: getPage(toLocation.search),
        })
    )
        router.resetNextScroll = false;
});

Sentry.init({
    autoSessionTracking: true,
    dsn: SENTRY.DSN,
    enabled: SENTRY.ENABLED === true,
    environment: SENTRY.ENV,
    integrations: [
        Sentry.replayIntegration(),
        Sentry.tanstackRouterBrowserTracingIntegration(router),
        Sentry.feedbackIntegration({ autoInject: false }),
    ],
    release: SENTRY.RELEASE,
    replaysOnErrorSampleRate: 1.0,
    replaysSessionSampleRate: 1.0, // should be adjust later on
    tracesSampleRate: SENTRY.SAMPLE_RATE,
});

declare module '@tanstack/react-router' {
    interface Register {
        router: typeof router;
    }
}

const rootElement = document.getElementById('root');
// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
const root = ReactDOM.createRoot(rootElement!);
root.render(
    <StrictMode>
        <QueryClientProvider client={queryClient}>
            <RouterProvider router={router} />
        </QueryClientProvider>
    </StrictMode>,
);
